/**
 * Registro central de moedas do Nautilus OS.
 * Cada documento (cotação, pedido, produto, proposta) tem UMA moeda.
 * Não há conversão de câmbio — valores são mantidos e formatados na moeda do documento.
 */

export const PRESET_CURRENCIES = [
  { code: 'BRL', name: 'Real', symbol: 'R$', locale: 'pt-BR', decimals: 2 },
  { code: 'USD', name: 'Dólar Americano', symbol: 'US$', locale: 'en-US', decimals: 2 },
  { code: 'EUR', name: 'Euro', symbol: '€', locale: 'de-DE', decimals: 2 },
  { code: 'NOK', name: 'Coroa Norueguesa', symbol: 'kr', locale: 'nb-NO', decimals: 2 },
  { code: 'CNY', name: 'Yuan (Renminbi)', symbol: '¥', locale: 'zh-CN', decimals: 2 },
];

const PRESET_CODES = new Set(PRESET_CURRENCIES.map((c) => c.code));

// Registro em memória de moedas customizadas (populado pela app via setCustomCurrencies)
let _customCurrencies = [];

export const setCustomCurrencies = (list) => {
  _customCurrencies = Array.isArray(list) ? list : [];
};

export const getCustomCurrencies = () => _customCurrencies;

/** Todas as moedas disponíveis (presets + customizadas) */
export const getAllCurrencies = () => [...PRESET_CURRENCIES, ..._customCurrencies];

/** Retorna metadados de uma moeda pelo código */
export const getCurrencyMeta = (code) => {
  const c = (code || 'BRL').toUpperCase();
  return (
    PRESET_CURRENCIES.find((m) => m.code === c) ||
    _customCurrencies.find((m) => m.code === c) ||
    PRESET_CURRENCIES[0]
  );
};

/** Símbolo da moeda */
export const getCurrencySymbol = (code) => getCurrencyMeta(code).symbol;

/**
 * Formata um valor na moeda informada, seguindo o padrão do país (presets)
 * ou símbolo + agrupamento padrão (customizadas).
 */
export const formatCurrency = (value, code = 'BRL') => {
  const num = typeof value === 'string' ? parseFloat(value) : (value || 0);
  const safe = isNaN(num) ? 0 : num;
  const meta = getCurrencyMeta(code);

  if (PRESET_CODES.has(meta.code)) {
    try {
      return new Intl.NumberFormat(meta.locale, {
        style: 'currency',
        currency: meta.code,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: meta.decimals,
        maximumFractionDigits: meta.decimals,
      }).format(safe);
    } catch (e) {
      // fallback
    }
  }

  // Moeda customizada (código não-ISO): símbolo + número
  const formatted = new Intl.NumberFormat(meta.locale || 'en-US', {
    minimumFractionDigits: meta.decimals ?? 2,
    maximumFractionDigits: meta.decimals ?? 2,
  }).format(safe);
  return `${meta.symbol} ${formatted}`;
};

/** Formatação resumida (K, M, B) mantendo o símbolo da moeda */
export const formatCurrencyShort = (value, code = 'BRL') => {
  const num = typeof value === 'string' ? parseFloat(value) : (value || 0);
  const safe = isNaN(num) ? 0 : num;
  const meta = getCurrencyMeta(code);
  const sym = meta.symbol;
  const abbr = (n, suffix) => {
    const v = (safe / n).toFixed(1).replace('.', meta.code === 'USD' || meta.code === 'CNY' ? '.' : ',');
    return `${sym} ${v}${suffix}`;
  };
  if (Math.abs(safe) >= 1e9) return abbr(1e9, 'B');
  if (Math.abs(safe) >= 1e6) return abbr(1e6, 'M');
  if (Math.abs(safe) >= 1e3) return abbr(1e3, 'K');
  return formatCurrency(safe, code);
};
